import React, { useState, useEffect, useRef } from 'react';
import { resultsAPI } from '../lib/api';
import { formatDate, getOrgColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import {
  Trophy,
  Plus,
  Trash2,
  TrendingUp,
  Medal,
  Target,
  Calendar,
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  AlertCircle,
  Info,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const ORGANIZATIONS = ['USAA', 'USAT', 'NFAA', 'TSAA', 'TFAA', 'ASA', 'IBO', 'OTHER'];
const EVENT_TYPES = ['Target', 'Field', '3D', 'Indoor', 'Outdoor'];

export default function Results() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [activeImportTab, setActiveImportTab] = useState('file');
  const [importType, setImportType] = useState('csv'); // 'csv', 'pdf', or 'url'
  const [urlImportData, setUrlImportData] = useState({ url: '', archerNames: '' });
  const [urlPreview, setUrlPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [myArchers, setMyArchers] = useState({ archer_names: [], role: '', loading: false });
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    tournament_id: '',
    tournament_name: '',
    org: '',
    event_date: '',
    event_type: '',
    score: '',
    max_score: '',
    placement: '',
    total_participants: '',
    notes: ''
  });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await resultsAPI.getAll();
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyArchers = async () => {
    if (myArchers.loading || myArchers.archer_names.length > 0) return;
    
    setMyArchers(prev => ({ ...prev, loading: true }));
    try {
      const response = await resultsAPI.getMyArchers();
      setMyArchers({
        archer_names: response.data.archer_names || [],
        role: response.data.role || '',
        loading: false
      });
      // Auto-populate archer names if we have them
      if (response.data.archer_names?.length > 0) {
        setUrlImportData(prev => ({
          ...prev,
          archerNames: response.data.archer_names.join('\n')
        }));
      }
    } catch (error) {
      console.error('Failed to fetch archers:', error);
      setMyArchers(prev => ({ ...prev, loading: false }));
    }
  };

  const handleTabChange = (tab) => {
    setActiveImportTab(tab);
    // Auto-fetch archers when switching to URL tab
    if (tab === 'url') {
      fetchMyArchers();
    }
  };

  const resetForm = () => {
    setFormData({
      tournament_id: '',
      tournament_name: '',
      org: '',
      event_date: '',
      event_type: '',
      score: '',
      max_score: '',
      placement: '',
      total_participants: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.tournament_name || !formData.org || !formData.event_date || !formData.score) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await resultsAPI.add({
        ...formData,
        tournament_id: formData.tournament_id || `manual-${Date.now()}`,
        score: parseInt(formData.score),
        max_score: formData.max_score ? parseInt(formData.max_score) : null,
        placement: formData.placement ? parseInt(formData.placement) : null,
        total_participants: formData.total_participants ? parseInt(formData.total_participants) : null
      });
      toast.success('Result added successfully');
      setDialogOpen(false);
      resetForm();
      fetchResults();
    } catch (error) {
      toast.error('Failed to add result');
    }
  };

  const handleDelete = async (resultId) => {
    if (!window.confirm('Delete this result?')) return;

    try {
      await resultsAPI.delete(resultId);
      setResults(results.filter((r) => r.id !== resultId));
      toast.success('Result deleted');
    } catch (error) {
      toast.error('Failed to delete result');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImportType('csv');
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      setCsvContent(content);
      setActiveImportTab('preview');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setImportType('pdf');
    setPdfFile(file);
    setActiveImportTab('preview');
  };

  const handleImportCSV = async () => {
    if (!csvContent.trim()) {
      toast.error('Please provide CSV content');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await resultsAPI.importCSV(csvContent);
      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} results`);
        fetchResults();
      } else {
        toast.warning('No results were imported. Check for errors.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Import failed';
      toast.error(message);
      setImportResult({ error: message });
    } finally {
      setImporting(false);
    }
  };

  const handleImportPDF = async () => {
    if (!pdfFile) {
      toast.error('Please select a PDF file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target?.result?.split(',')[1]; // Remove data:... prefix
          const response = await resultsAPI.importPDF(base64, pdfFile.name);
          setImportResult(response.data);
          
          if (response.data.imported_count > 0) {
            toast.success(`Successfully imported ${response.data.imported_count} results from PDF`);
            fetchResults();
          } else if (response.data.status === 'no_results') {
            toast.warning('Could not extract results from the PDF');
            setImportResult({ 
              ...response.data, 
              warning: 'No results found in PDF. Check the parsed fields below.'
            });
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'PDF import failed';
          toast.error(message);
          setImportResult({ error: message });
        } finally {
          setImporting(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read PDF file');
        setImporting(false);
      };
      reader.readAsDataURL(pdfFile);
    } catch (error) {
      toast.error('Failed to process PDF file');
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (importType === 'pdf') {
      await handleImportPDF();
    } else if (importType === 'url') {
      await handleImportURL();
    } else {
      await handleImportCSV();
    }
  };

  const handlePreviewURL = async () => {
    if (!urlImportData.url) {
      toast.error('Please enter a URL');
      return;
    }

    setLoadingPreview(true);
    setUrlPreview(null);

    try {
      const response = await resultsAPI.previewURL(urlImportData.url);
      setUrlPreview(response.data);
      toast.success(`Found ${response.data.total_entries} archers in results`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to preview URL';
      toast.error(message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImportURL = async () => {
    if (!urlImportData.url) {
      toast.error('Please enter a URL');
      return;
    }

    if (!urlImportData.archerNames.trim()) {
      toast.error('Please enter archer names to search for');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const archerNames = urlImportData.archerNames
        .split('\n')
        .map(n => n.trim())
        .filter(n => n.length > 0);

      const response = await resultsAPI.importURL(urlImportData.url, archerNames);
      setImportResult(response.data);

      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} results`);
        fetchResults();
      } else {
        toast.warning('No matching archers found in results');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'URL import failed';
      toast.error(message);
      setImportResult({ error: message });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await resultsAPI.getCSVTemplate();
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'results_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetImportDialog = () => {
    setCsvContent('');
    setPdfFile(null);
    setImportResult(null);
    setActiveImportTab('file');
    setImportType('csv');
    setUrlImportData({ url: '', archerNames: '' });
    setUrlPreview(null);
    setMyArchers({ archer_names: [], role: '', loading: false });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
    }
  };

  // Calculate statistics
  const totalEvents = results.length;
  const avgScore = totalEvents > 0
    ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / totalEvents)
    : 0;
  const bestPlacement = results
    .filter((r) => r.placement)
    .reduce((best, r) => (r.placement < best ? r.placement : best), Infinity);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="results-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tournament Results</h1>
          <p className="text-slate-600 mt-1">Track and analyze your competition performance</p>
        </div>
        <div className="flex gap-2">
          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) resetImportDialog();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-results-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Import Results
                </DialogTitle>
              </DialogHeader>
              
              <Tabs value={activeImportTab} onValueChange={handleTabChange} className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="file" data-testid="import-tab-file">Upload File</TabsTrigger>
                  <TabsTrigger value="url" data-testid="import-tab-url">Import from URL</TabsTrigger>
                  <TabsTrigger value="preview" data-testid="import-tab-preview">Preview & Import</TabsTrigger>
                  <TabsTrigger value="help" data-testid="import-tab-help">Help</TabsTrigger>
                </TabsList>
                
                <TabsContent value="file" className="space-y-4 mt-4">
                  {/* PDF Upload - Between Ends Scorecards */}
                  <div className="border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={handlePdfUpload}
                      data-testid="pdf-file-input"
                    />
                    <FileText className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                    <h3 className="text-base font-medium text-slate-900 mb-1">Between Ends PDF Scorecard</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Upload scorecards exported from betweenends.com
                    </p>
                    <Button
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => pdfInputRef.current?.click()}
                      data-testid="select-pdf-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Select PDF
                    </Button>
                    {pdfFile && (
                      <div className="mt-3 flex items-center justify-center gap-2 text-sm text-blue-700">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{pdfFile.name}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-sm text-slate-500">or upload CSV</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  
                  {/* CSV Upload */}
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="csv-file-input"
                    />
                    <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <h3 className="text-base font-medium text-slate-900 mb-1">CSV File</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Upload a spreadsheet with your results
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="select-file-btn"
                    >
                      Select CSV
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-sm text-slate-500">or paste CSV content</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  
                  <Textarea
                    placeholder="Paste your CSV content here..."
                    value={csvContent}
                    onChange={(e) => { setCsvContent(e.target.value); setImportType('csv'); }}
                    rows={4}
                    className="font-mono text-sm"
                    data-testid="csv-paste-input"
                  />
                  
                  <div className="flex justify-between items-center">
                    <Button
                      variant="link"
                      onClick={handleDownloadTemplate}
                      className="text-sm"
                      data-testid="download-template-btn"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download CSV Template
                    </Button>
                    <Button
                      onClick={() => setActiveImportTab('preview')}
                      disabled={!csvContent.trim() && !pdfFile}
                      data-testid="continue-to-preview-btn"
                    >
                      Continue to Preview
                    </Button>
                  </div>
                </TabsContent>
                
                {/* URL Import Tab */}
                <TabsContent value="url" className="space-y-4 mt-4">
                  <div className="border-2 border-dashed border-purple-200 bg-purple-50/50 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-6 h-6 text-purple-600" />
                      <h3 className="text-base font-medium text-slate-900">Import from Results URL</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Import results directly from tournament result pages (e.g., TFAA results at texasfieldarchery.org)
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="results-url">Results Page URL</Label>
                        <Input
                          id="results-url"
                          placeholder="https://texasfieldarchery.org/results/1333"
                          value={urlImportData.url}
                          onChange={(e) => setUrlImportData({ ...urlImportData, url: e.target.value })}
                          className="mt-1"
                          data-testid="url-import-input"
                        />
                      </div>
                      
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={handlePreviewURL}
                          disabled={!urlImportData.url || loadingPreview}
                          className="text-purple-700 border-purple-300"
                          data-testid="preview-url-btn"
                        >
                          {loadingPreview ? 'Loading...' : 'Preview Results'}
                        </Button>
                      </div>
                      
                      {urlPreview && (
                        <Alert className="border-purple-200 bg-purple-50">
                          <Info className="w-4 h-4 text-purple-600" />
                          <AlertDescription>
                            <strong>{urlPreview.tournament_name}</strong> ({urlPreview.event_date})
                            <br />
                            Found <strong>{urlPreview.total_entries}</strong> archers in results
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label htmlFor="archer-names">
                            Archer Names to Search ({urlImportData.archerNames.split('\n').filter(n => n.trim()).length} names)
                          </Label>
                          {myArchers.loading && (
                            <span className="text-xs text-purple-600">Loading your archers...</span>
                          )}
                        </div>
                        {myArchers.archer_names.length > 0 ? (
                          <p className="text-xs text-emerald-600 mb-1">
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            Auto-loaded {myArchers.archer_names.length} {myArchers.role === 'parent' ? 'family' : myArchers.role === 'coach' ? 'club' : ''} archer{myArchers.archer_names.length > 1 ? 's' : ''}. Edit below if needed.
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500 mb-1">
                            Enter the names of archers to match against the results
                          </p>
                        )}
                        <Textarea
                          id="archer-names"
                          placeholder="John Smith&#10;Jane Doe&#10;Mike Johnson"
                          value={urlImportData.archerNames}
                          onChange={(e) => setUrlImportData({ ...urlImportData, archerNames: e.target.value })}
                          rows={4}
                          className="mt-1"
                          data-testid="archer-names-input"
                        />
                      </div>
                      
                      {urlPreview && urlPreview.archers && urlPreview.archers.length > 0 && (
                        <div className="bg-slate-50 rounded-lg p-3 max-h-[150px] overflow-y-auto">
                          <p className="text-xs font-medium text-slate-600 mb-2">Sample archers in results:</p>
                          <div className="text-xs text-slate-500 space-y-1">
                            {urlPreview.archers.slice(0, 10).map((a, i) => (
                              <div key={i} className="flex justify-between">
                                <span>{a.name}</span>
                                <span className="text-slate-400">{a.score} pts - #{a.placement}</span>
                              </div>
                            ))}
                            {urlPreview.archers.length > 10 && (
                              <p className="text-slate-400 italic">... and {urlPreview.total_entries - 10} more</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <Button
                        onClick={handleImportURL}
                        disabled={!urlImportData.url || !urlImportData.archerNames.trim() || importing}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        data-testid="import-url-btn"
                      >
                        {importing ? 'Importing...' : 'Import Matching Results'}
                      </Button>
                      
                      {importResult && importResult.matched_results && (
                        <div className="space-y-2">
                          <Alert className={importResult.imported_count > 0 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}>
                            {importResult.imported_count > 0 ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                            )}
                            <AlertDescription>
                              {importResult.imported_count > 0 
                                ? `Imported ${importResult.imported_count} results from ${importResult.tournament_name}`
                                : importResult.message || 'No matching archers found'
                              }
                            </AlertDescription>
                          </Alert>
                          
                          {importResult.matched_results.length > 0 && (
                            <div className="bg-emerald-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-emerald-700 mb-2">Imported Results:</p>
                              <div className="space-y-1">
                                {importResult.matched_results.map((r, i) => (
                                  <div key={i} className="text-sm text-emerald-800 flex justify-between">
                                    <span>{r.archer_name}</span>
                                    <span>{r.score} pts - #{r.placement} ({r.division} / {r.style})</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="preview" className="space-y-4 mt-4">
                  {(csvContent || pdfFile) ? (
                    <>
                      <Alert className={importType === 'pdf' ? "border-blue-200 bg-blue-50" : ""}>
                        <Info className="w-4 h-4" />
                        <AlertDescription>
                          {importType === 'pdf' 
                            ? `Ready to import from PDF: ${pdfFile?.name}. Click "Import Results" to parse and import.`
                            : 'Review your CSV content below. Click "Import Results" to proceed.'
                          }
                        </AlertDescription>
                      </Alert>
                      
                      {importType === 'csv' && csvContent && (
                        <div className="bg-slate-50 rounded-lg p-4 max-h-[200px] overflow-auto">
                          <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">
                            {csvContent.slice(0, 2000)}
                            {csvContent.length > 2000 && '...'}
                          </pre>
                        </div>
                      )}
                      
                      {importType === 'pdf' && pdfFile && (
                        <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-4">
                          <FileText className="w-12 h-12 text-blue-500" />
                          <div>
                            <p className="font-medium text-slate-900">{pdfFile.name}</p>
                            <p className="text-sm text-slate-500">
                              {(pdfFile.size / 1024).toFixed(1)} KB • PDF Document
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Will extract archer name, event, date, scores from Between Ends format
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {importResult && (
                        <div className="space-y-3">
                          {importResult.imported_count > 0 && (
                            <Alert className="border-emerald-200 bg-emerald-50">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              <AlertDescription className="text-emerald-800">
                                Successfully imported {importResult.imported_count} result(s)
                                {importResult.imported_results && (
                                  <ul className="mt-2 text-xs space-y-1">
                                    {importResult.imported_results.slice(0, 5).map((r, idx) => (
                                      <li key={idx}>{r.tournament_name} - Score: {r.score}</li>
                                    ))}
                                  </ul>
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {importResult.parsed_fields && (
                            <Alert className="border-slate-200 bg-slate-50">
                              <Info className="w-4 h-4 text-slate-600" />
                              <AlertDescription className="text-slate-700">
                                <p className="font-medium mb-1">Parsed from PDF:</p>
                                <ul className="text-xs space-y-1">
                                  {importResult.parsed_fields.event_name && (
                                    <li>Event: {importResult.parsed_fields.event_name}</li>
                                  )}
                                  {importResult.parsed_fields.archer_name && (
                                    <li>Archer: {importResult.parsed_fields.archer_name}</li>
                                  )}
                                  {importResult.parsed_fields.event_date && (
                                    <li>Date: {importResult.parsed_fields.event_date}</li>
                                  )}
                                  {importResult.parsed_fields.total_score && (
                                    <li>Total Score: {importResult.parsed_fields.total_score}</li>
                                  )}
                                  {importResult.parsed_fields.division && (
                                    <li>Division: {importResult.parsed_fields.division}</li>
                                  )}
                                </ul>
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {importResult.warning && (
                            <Alert className="border-amber-200 bg-amber-50">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <AlertDescription className="text-amber-800">
                                {importResult.warning}
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {importResult.error_count > 0 && (
                            <Alert className="border-amber-200 bg-amber-50">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <AlertDescription className="text-amber-800">
                                {importResult.error_count} row(s) had errors:
                                <ul className="mt-2 text-xs space-y-1">
                                  {importResult.errors?.map((err, idx) => (
                                    <li key={idx}>Row {err.row}: {err.error}</li>
                                  ))}
                                </ul>
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          {importResult.error && (
                            <Alert className="border-red-200 bg-red-50">
                              <AlertCircle className="w-4 h-4 text-red-600" />
                              <AlertDescription className="text-red-800">
                                {importResult.error}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <Button
                          variant="outline"
                          onClick={() => setActiveImportTab('file')}
                        >
                          Back
                        </Button>
                        <Button
                          className="btn-primary"
                          onClick={handleImport}
                          disabled={importing}
                          data-testid="import-btn"
                        >
                          {importing ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              {importType === 'pdf' ? 'Parsing PDF...' : 'Importing...'}
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Import Results
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No file selected</p>
                      <Button
                        variant="link"
                        onClick={() => setActiveImportTab('file')}
                        className="mt-2"
                      >
                        Go back to upload
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="help" className="space-y-4 mt-4">
                  {/* PDF Import Info */}
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Between Ends PDF Import
                      </CardTitle>
                      <CardDescription>
                        Upload scorecards directly from betweenends.com
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-slate-600">
                        We automatically extract the following from Between Ends PDF scorecards:
                      </p>
                      <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                        <li>Event name and organization</li>
                        <li>Event date and location</li>
                        <li>Archer name and division</li>
                        <li>Round scores and total score</li>
                        <li>10s and 9s counts</li>
                      </ul>
                      <p className="text-xs text-blue-600">
                        Tip: Download your scorecard from betweenends.com after each competition
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* CSV Format Guide */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">CSV Format Guide</CardTitle>
                      <CardDescription>
                        Alternatively, use a CSV file with these columns
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-3 gap-2 font-medium text-slate-700 border-b pb-2">
                          <span>Column</span>
                          <span>Required</span>
                          <span>Description</span>
                        </div>
                        {[
                          { name: 'tournament_name', required: true, desc: 'Name of the tournament' },
                          { name: 'org', required: true, desc: 'USAA, NFAA, TSAA, TFAA, ASA, IBO, USAT' },
                          { name: 'event_date', required: true, desc: 'YYYY-MM-DD or MM/DD/YYYY' },
                          { name: 'event_type', required: false, desc: 'Target, Field, 3D, Indoor, Outdoor' },
                          { name: 'score', required: true, desc: 'Your score' },
                          { name: 'max_score', required: false, desc: 'Maximum possible score' },
                          { name: 'placement', required: false, desc: 'Your placement/rank' },
                          { name: 'total_participants', required: false, desc: 'Field size' },
                          { name: 'notes', required: false, desc: 'Additional notes' },
                        ].map((col) => (
                          <div key={col.name} className="grid grid-cols-3 gap-2 text-slate-600">
                            <code className="text-xs bg-slate-100 px-1 rounded">{col.name}</code>
                            <span>{col.required ? <Badge variant="destructive" className="text-xs">Required</Badge> : <Badge variant="outline" className="text-xs">Optional</Badge>}</span>
                            <span className="text-xs">{col.desc}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Example CSV</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs font-mono bg-slate-50 p-4 rounded-lg overflow-x-auto">
{`tournament_name,org,event_date,event_type,score,max_score,placement,total_participants,notes
Vegas Shoot 2024,NFAA,2024-02-15,Indoor,298,300,5,150,Great performance
State Championship,USAA,2024-03-20,Target,625,720,3,45,Outdoor target
3D Nationals,ASA,2024-06-10,3D,320,400,12,200,Hot weather`}
                      </pre>
                    </CardContent>
                  </Card>
                  
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      data-testid="download-template-help-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Sample Template
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          
          {/* Add Result Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-result-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Result
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Tournament Result</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label>Tournament Name *</Label>
                  <Input
                    placeholder="Enter tournament name"
                    value={formData.tournament_name}
                    onChange={(e) => setFormData({ ...formData, tournament_name: e.target.value })}
                    data-testid="result-tournament-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization *</Label>
                    <Select
                      value={formData.org}
                      onValueChange={(value) => setFormData({ ...formData, org: value })}
                    >
                      <SelectTrigger data-testid="result-org-select">
                        <SelectValue placeholder="Select org" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORGANIZATIONS.map((org) => (
                          <SelectItem key={org} value={org}>
                            {org}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Event Type *</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                    >
                      <SelectTrigger data-testid="result-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Event Date *</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    data-testid="result-date-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Score *</Label>
                    <Input
                      type="number"
                      placeholder="Your score"
                      value={formData.score}
                      onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                      data-testid="result-score-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Max Score</Label>
                    <Input
                      type="number"
                      placeholder="Maximum possible"
                      value={formData.max_score}
                      onChange={(e) => setFormData({ ...formData, max_score: e.target.value })}
                      data-testid="result-max-score-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Placement</Label>
                    <Input
                      type="number"
                      placeholder="Your rank"
                      value={formData.placement}
                      onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                      data-testid="result-placement-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Total Participants</Label>
                    <Input
                      type="number"
                      placeholder="In your division"
                      value={formData.total_participants}
                      onChange={(e) => setFormData({ ...formData, total_participants: e.target.value })}
                      data-testid="result-participants-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    placeholder="Any additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="result-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button className="btn-primary" onClick={handleSubmit} data-testid="save-result-btn">
                  <Trophy className="w-4 h-4 mr-2" />
                  Save Result
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Events</p>
                <p className="text-2xl font-bold text-slate-900">{totalEvents}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Average Score</p>
                <p className="text-2xl font-bold text-slate-900">{avgScore}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Medal className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Best Placement</p>
                <p className="text-2xl font-bold text-slate-900">
                  {bestPlacement === Infinity ? '-' : `#${bestPlacement}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results List */}
      {results.length > 0 ? (
        <div className="space-y-4">
          {results.map((result) => (
            <Card
              key={result.id}
              className="card-base"
              data-testid={`result-card-${result.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900 text-lg">
                        {result.tournament_name}
                      </h3>
                      <Badge className={getOrgColor(result.org)}>{result.org}</Badge>
                      <Badge variant="outline">{result.event_type}</Badge>
                      {result.source === 'csv_import' && (
                        <Badge variant="secondary" className="text-xs">
                          <FileSpreadsheet className="w-3 h-3 mr-1" />
                          Imported
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(result.event_date)}
                      </span>
                    </div>
                    {result.notes && (
                      <p className="mt-2 text-sm text-slate-500 italic">{result.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Score</p>
                      <p className="text-2xl font-bold text-primary">
                        {result.score}
                        {result.max_score && (
                          <span className="text-sm text-slate-400 font-normal">
                            /{result.max_score}
                          </span>
                        )}
                      </p>
                    </div>

                    {result.placement && (
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Place</p>
                        <p className="text-2xl font-bold text-amber-600">
                          #{result.placement}
                          {result.total_participants && (
                            <span className="text-sm text-slate-400 font-normal">
                              /{result.total_participants}
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleDelete(result.id)}
                      data-testid={`delete-result-${result.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Target className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No results recorded</h3>
            <p className="text-slate-500 mb-4">
              Start tracking your tournament performance
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Import from CSV
              </Button>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Result
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
